import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useWebSocket } from "../hooks/useWebSocket";
import { tradingApi } from "../services/api";
import axios from "axios";
import PropTypes from "prop-types";
import { useAuth } from "../contexts/AuthContext";

const TradingContext = createContext();

// Helper to convert 'BTC/USD' to 'BTCUSDT'
const formatSymbol = (symbol) => symbol.replace("/", "");

// Cache for symbols to avoid repeated API calls
const SYMBOLS_CACHE_KEY = "trading_symbols_cache";
const SYMBOLS_CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const TradingProvider = ({ children }) => {
  const [symbols, setSymbols] = useState([]);
  const [selectedSymbol, setSelectedSymbol] = useState("BTCUSDT");
  const [selectedSymbolDisplay, setSelectedSymbolDisplay] = useState("BTC/USDT");
  const [marketData, setMarketData] = useState({});
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [strategies, setStrategies] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [orderBook, setOrderBook] = useState({ bids: [], asks: [], lastPrice: 0 });
  const [orderPrice, setOrderPrice] = useState(0);
  const [orderAmount, setOrderAmount] = useState(0);
  const [balance, setBalance] = useState({ free: 0 });
  const [timeframe, setTimeframe] = useState("1h");
  const [exchanges, setExchanges] = useState([]);
  const [activeExchange, setActiveExchange] = useState(null);
  // Throttle timestamp kept in a ref, NOT state: updating it must not change the
  // identity of fetchTradingData, otherwise the mount effect (dep: [fetchTradingData])
  // would re-fire on every successful fetch and hammer the API in an infinite loop.
  const lastDataFetchRef = useRef(0);
  const { isAuthenticated } = useAuth();

  // Memoized values to prevent unnecessary re-renders
  const memoizedPositions = useMemo(() => positions, [positions]);
  const memoizedOrders = useMemo(() => orders, [orders]);
  const memoizedBalance = useMemo(() => balance, [balance]);

  // Fetch exchanges on startup (only once)
  useEffect(() => {
    const fetchExchanges = async () => {
      try {
        const fetchedExchanges = await tradingApi.getAvailableExchanges();
        setExchanges(fetchedExchanges);
        // Set the first active exchange as the default
        const defaultActive = fetchedExchanges.find((ex) => ex.is_active);
        if (defaultActive) {
          setActiveExchange(defaultActive);
        } else if (fetchedExchanges.length > 0) {
          setActiveExchange(fetchedExchanges[0]); // Fallback to the first one
        }
      } catch (error) {
        console.error("Failed to fetch exchanges:", error);
      }
    };
    fetchExchanges();
  }, []);

  const fetchTradingData = useCallback(
    async (force = false) => {
      // Only fetch if authenticated and not too frequent
      if (!isAuthenticated) {
        return;
      }

      const now = Date.now();
      const timeSinceLastFetch = now - lastDataFetchRef.current;

      // Don't fetch more than once every 30 seconds unless forced
      if (!force && timeSinceLastFetch < 30000) {
        return;
      }

      console.log("--- Fetching trading data ---");
      try {
        setIsLoading(true);

        // Use the selected exchange ID for all trading operations
        const exchangeId = activeExchange?.id || null;

        const [positionsData, ordersData, balanceData] = await Promise.all([
          tradingApi.getPositions(exchangeId),
          tradingApi.getOrders(exchangeId),
          tradingApi.getBalance(exchangeId),
        ]);

        console.log("API Response - Positions:", positionsData);
        console.log("API Response - Orders:", ordersData);
        console.log("API Response - Balance:", balanceData);

        // Set positions data
        setPositions(positionsData);
        console.log("State Updated - Positions:", positionsData);

        // Set orders data
        setOrders(ordersData);
        console.log("State Updated - Orders:", ordersData);

        // Set balance data
        if (balanceData && balanceData.length > 0) {
          // Store all balances as a map keyed by asset
          const balanceMap = {};
          balanceData.forEach((b) => {
            if (b.asset) balanceMap[b.asset] = b;
          });
          setBalance(balanceMap);
          console.log("State Updated - Balance:", balanceMap);
        }

        lastDataFetchRef.current = now;
      } catch (err) {
        const errorMessage =
          err.response?.data?.detail || err.message || "An unknown error occurred";
        setError(errorMessage);
        console.error("Error fetching trading data:", errorMessage, err.response || err);
      } finally {
        setIsLoading(false);
      }
    },
    [isAuthenticated, activeExchange]
  );

  // Initial data fetch only (no polling)
  useEffect(() => {
    fetchTradingData(true);
  }, [fetchTradingData]);

  // Fetch dynamic symbol list on mount (with improved caching)
  useEffect(() => {
    const fetchSymbols = async () => {
      try {
        const exchangeId = activeExchange?.id || null;
        const cacheKey = `${SYMBOLS_CACHE_KEY}_${exchangeId || "default"}`;

        // Check cache first
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const { data, timestamp } = JSON.parse(cached);
          if (Date.now() - timestamp < SYMBOLS_CACHE_DURATION) {
            setSymbols(data);
            return;
          }
        }

        // Use the trading API with exchange ID
        const data = await tradingApi.getSymbols(exchangeId);
        setSymbols(data);

        // Cache the result
        localStorage.setItem(
          cacheKey,
          JSON.stringify({
            data,
            timestamp: Date.now(),
          })
        );
      } catch (error) {
        console.error("Error fetching symbols:", error);
        // Try to use cached data even if expired
        const exchangeId = activeExchange?.id || null;
        const cacheKey = `${SYMBOLS_CACHE_KEY}_${exchangeId || "default"}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const { data } = JSON.parse(cached);
          setSymbols(data);
        }
      }
    };

    if (activeExchange) {
      fetchSymbols();
    }
  }, [activeExchange]);

  // Phase 7: Authenticated WebSocket with real-time push (replaces polling)
  const {
    lastMessage,
    isConnected: wsConnected,
    subscribe,
    watchPrices,
  } = useWebSocket("/ws/trading");

  // Subscribe to channels once connected
  useEffect(() => {
    if (wsConnected) {
      subscribe("prices");
      subscribe("trades");
      subscribe("bot_status");
      // Watch the currently selected symbol for live prices
      if (selectedSymbol) {
        watchPrices([selectedSymbol]);
      }
    }
  }, [wsConnected, subscribe, watchPrices, selectedSymbol]);

  // Handle incoming WebSocket messages (all pre-parsed JSON)
  useEffect(() => {
    if (!lastMessage) return;

    const data = lastMessage; // Already parsed by the hook

    if (data.type === "prices" && data.data) {
      // Live price updates from CandleStore
      setMarketData((prev) => {
        const updated = { ...prev };
        for (const [symbol, price] of Object.entries(data.data)) {
          if (updated[symbol]) {
            updated[symbol] = { ...updated[symbol], price };
          } else {
            updated[symbol] = { price };
          }
        }
        return updated;
      });
    } else if (data.type === "trade") {
      // Trade executed by a bot — refresh orders
      console.log("[WS] Trade event:", data.data);
    } else if (data.type === "bot_status") {
      // Bot started/stopped — UI can react to this
      console.log("[WS] Bot status:", data.bot_id, data.status);
    } else if (data.type === "market_data") {
      // Legacy market data format (backward compat)
      setMarketData((prev) => ({
        ...prev,
        [data.symbol]: data.data,
      }));
    } else if (data.type === "order_update") {
      setOrders((prevOrders) => {
        const index = prevOrders.findIndex((o) => o.id === data.order.id);
        if (index > -1) {
          const updatedOrders = [...prevOrders];
          updatedOrders[index] = data.order;
          return updatedOrders;
        } else {
          return [...prevOrders, data.order];
        }
      });
    } else if (data.type === "position_update") {
      setPositions((prevPositions) => {
        const index = prevPositions.findIndex((p) => p.asset === data.position.asset);
        const newPosition = {
          ...data.position,
          total_value_usd: data.position.total,
          free_value_usd: data.position.free,
          used_value_usd: data.position.used,
        };

        if (index > -1) {
          const updatedPositions = [...prevPositions];
          updatedPositions[index] = newPosition;
          return updatedPositions;
        } else {
          return [...prevPositions, newPosition];
        }
      });
    }
  }, [lastMessage]);

  // Load initial data when selectedSymbol changes (debounced)
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setIsLoading(true);
        const exchangeId = activeExchange?.id || null;

        const [strategiesData, marketDataResp] = await Promise.all([
          tradingApi.getStrategies(),
          tradingApi.getMarketData(selectedSymbol, exchangeId),
        ]);
        setStrategies(strategiesData);
        setMarketData({ [selectedSymbol]: marketDataResp });
      } catch (error) {
        setError(error.message);
        console.error("Error loading initial data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (selectedSymbol) {
      // Debounce the symbol change to avoid rapid API calls
      const timeoutId = setTimeout(loadInitialData, 300);
      return () => clearTimeout(timeoutId);
    }
  }, [selectedSymbol, activeExchange]);

  // Update display symbol when selectedSymbol changes
  useEffect(() => {
    if (symbols.length > 0 && selectedSymbol) {
      const found = symbols.find((s) => s.binance === selectedSymbol);
      if (found) setSelectedSymbolDisplay(found.display);
    }
  }, [selectedSymbol, symbols]);

  // Update order book when selectedSymbol changes
  useEffect(() => {
    if (!selectedSymbol) return;
    // Reset order book to empty while loading new data
    setOrderBook({ bids: [], asks: [], lastPrice: 0 });
    // WebSocket and REST fetch logic for order book is already handled elsewhere
  }, [selectedSymbol]);

  // Trading operations
  const placeOrder = async (orderData) => {
    try {
      const exchangeId = activeExchange?.id || null;
      const response = await tradingApi.placeOrder(orderData, exchangeId);
      await fetchTradingData(true); // Force a refresh
      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const cancelOrder = async (orderId, symbol) => {
    try {
      const exchangeId = activeExchange?.id || null;
      await tradingApi.cancelOrder(orderId, symbol, exchangeId);
      await fetchTradingData(true); // Force a refresh
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const updatePosition = async (positionId, updates) => {
    try {
      const response = await tradingApi.updatePosition(positionId, updates);
      setPositions((prev) => prev.map((pos) => (pos.id === positionId ? response : pos)));
      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const closePosition = async (positionId) => {
    try {
      await tradingApi.closePosition(positionId);
      setPositions((prev) => prev.filter((pos) => pos.id !== positionId));
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const startStrategy = async (strategyId) => {
    try {
      const response = await tradingApi.startStrategy(strategyId);
      setStrategies((prev) => prev.map((strat) => (strat.id === strategyId ? response : strat)));
      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const stopStrategy = async (strategyId) => {
    try {
      const response = await tradingApi.stopStrategy(strategyId);
      setStrategies((prev) => prev.map((strat) => (strat.id === strategyId ? response : strat)));
      return response;
    } catch (error) {
      setError(error.message);
      throw error;
    }
  };

  const value = {
    symbols,
    selectedSymbol,
    setSelectedSymbol,
    marketData,
    setMarketData,
    positions: memoizedPositions,
    orders: memoizedOrders,
    strategies,
    selectedSymbolDisplay,
    setSelectedSymbolDisplay,
    isLoading,
    error,
    placeOrder,
    cancelOrder,
    updatePosition,
    closePosition,
    startStrategy,
    stopStrategy,
    orderBook,
    setOrderBook,
    orderPrice,
    setOrderPrice,
    orderAmount,
    setOrderAmount,
    balance: memoizedBalance,
    setBalance,
    timeframe,
    setTimeframe,
    exchanges,
    activeExchange,
    setActiveExchange,
    fetchTradingData, // Expose for manual refresh
    watchPrices, // WS: subscribe to live price symbols
    wsConnected, // WS connection status
  };

  return <TradingContext.Provider value={value}>{children}</TradingContext.Provider>;
};

TradingProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export const useTrading = () => {
  const context = useContext(TradingContext);
  if (!context) {
    throw new Error("useTrading must be used within a TradingProvider");
  }
  return context;
};
